import { globalStores } from '@/editor/stores';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { Color, Vector2 } from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import BaseModel2d from './BaseModel2d';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { Line2 } from 'three/examples/jsm/Addons.js';
import { isNumber } from 'lodash-es';
import { IBoxOutlineParams } from '../type';

export class BoxOutlineModel extends BaseModel2d {
  private material: LineMaterial = new LineMaterial({
    color: new Color(0x000000),
    depthTest: false,
    transparent: true,
    linewidth: 1
  });
  constructor() {
    super();

    this.setDisplayOrder(ENTITY_RENDER_ORDER.selectedOutline);
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  update(params: Partial<IBoxOutlineParams>) {
    if (params.color) {
      this.material.color = new Color(params.color);
    }
    if (isNumber(params.linewidth)) {
      this.material.linewidth = params.linewidth;
    }
    if (isNumber(params.x)) {
      this._modelGroup.position.x = params.x / pixelRatio2d;
    }
    if (isNumber(params.y)) {
      this._modelGroup.position.y = params.y / pixelRatio2d;
    }
    if (isNumber(params.rotation)) {
      this._modelGroup.rotation.z = params.rotation;
    }
  }

  createModel(params: IBoxOutlineParams) {
    const width = params.width / pixelRatio2d;
    const thickness = params.thickness / pixelRatio2d;
    const x = params.x / pixelRatio2d;
    const y = params.y / pixelRatio2d;
    const vertices = [
      new Vector2(-width / 2, -thickness / 2),
      new Vector2(width / 2, -thickness / 2),
      new Vector2(width / 2, thickness / 2),
      new Vector2(-width / 2, thickness / 2),
      new Vector2(-width / 2, -thickness / 2)
    ];
    const color = new Color(params.color);

    const outlineGeometry = new LineGeometry();
    outlineGeometry.setFromPoints(vertices);
    this.material.color = color;
    this.material.linewidth = params.linewidth;
    const outline = new Line2(outlineGeometry, this.material);

    outline.renderOrder = params.renderOrder ?? ENTITY_RENDER_ORDER.selectedOutline;

    this._modelGroup.add(outline);
    this._modelGroup.position.setX(x);
    this._modelGroup.position.setY(y);
    this._modelGroup.rotation.z = params.rotation;
  }
}
